import { PT_PER_MM, type DisplayedBox } from './placement-math';

/** What the box may become: its shortest side, and whether the picture keeps its proportions. */
export interface BoxLimits {
  readonly keepRatio: boolean;
  /** Width over height of the picture. */
  readonly ratio: number;
  /** The shortest side a box may have, in points. */
  readonly minPt: number;
  /** With the proportions kept, the narrowest width whose height still reaches `minPt`. */
  readonly minWidthKept: number;
}

export type HandleKind = 'move' | 'rotate' | 'resize';
export type Field = 'x' | 'y' | 'w' | 'h' | 'angle';

export const round1 = (n: number): number => Math.round(n * 10) / 10;

const minWidth = (l: BoxLimits): number => (l.keepRatio ? l.minWidthKept : l.minPt);

/** A box whose short side is below `target` CSS px is grown to it, keeping its proportions. */
export function growBox(b: DisplayedBox, scale: number, target: number): DisplayedBox {
  const short = Math.min(b.width, b.height) * scale;
  if (short >= target) return b;
  const k = target / short;
  return { ...b, width: b.width * k, height: b.height * k };
}

export function rotateTo(b: DisplayedBox, p: { x: number; y: number }): DisplayedBox {
  return {
    ...b,
    angle: Math.round((Math.atan2(p.y - b.cy, p.x - b.cx) * 180) / Math.PI + 90),
  };
}

/** The corner handle at `p` (page points) sets the half-size along the box's own axes. */
export function resizeTo(b: DisplayedBox, p: { x: number; y: number }, l: BoxLimits): DisplayedBox {
  const a = (-b.angle * Math.PI) / 180;
  const dx = p.x - b.cx;
  const dy = p.y - b.cy;
  const lx = Math.abs(dx * Math.cos(a) - dy * Math.sin(a));
  const ly = Math.abs(dx * Math.sin(a) + dy * Math.cos(a));
  const width = Math.max(minWidth(l), lx * 2);
  const height = l.keepRatio ? width / l.ratio : Math.max(l.minPt, ly * 2);
  return { ...b, width, height };
}

/** An arrow key on a handle: Shift takes the bigger step. */
export function nudge(
  b: DisplayedBox,
  kind: HandleKind,
  dir: readonly number[],
  shift: boolean,
  l: BoxLimits,
): DisplayedBox {
  const step = shift ? 10 : 1;
  if (kind === 'move') {
    return { ...b, cx: b.cx + dir[0]! * step * PT_PER_MM, cy: b.cy + dir[1]! * step * PT_PER_MM };
  }
  if (kind === 'rotate') return { ...b, angle: b.angle + (dir[0]! || dir[1]!) * (shift ? 15 : 1) };
  const width = Math.max(minWidth(l), b.width + (dir[0]! || -dir[1]!) * step * PT_PER_MM);
  return { ...b, width, height: l.keepRatio ? width / l.ratio : b.height };
}

/** A measure typed in millimetres (or degrees), applied to the box's top-left-based fields. */
export function applyField(
  b: DisplayedBox,
  field: Field,
  value: number,
  l: BoxLimits,
): DisplayedBox {
  if (field === 'x') return { ...b, cx: value * PT_PER_MM + b.width / 2 };
  if (field === 'y') return { ...b, cy: value * PT_PER_MM + b.height / 2 };
  if (field === 'angle') return { ...b, angle: value };
  if (field === 'w') {
    const width = Math.max(minWidth(l), value * PT_PER_MM);
    return { ...b, width, height: l.keepRatio ? width / l.ratio : b.height };
  }
  const height = Math.max(l.keepRatio ? l.minWidthKept / l.ratio : l.minPt, value * PT_PER_MM);
  return { ...b, height, width: l.keepRatio ? height * l.ratio : b.width };
}
